import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import { Autocomplete, Button, Stack, TextField } from "@mui/material";
import { forwardRef, useEffect, useState } from "react";
import Modal, { CommonModalProps } from "..";
import { assetService, cacheService, orderService, userService, workflowService } from "../../../Core/Api/services";
import { FollowerDto, FollowersGroupedDto } from "../../../Core/Api/Types/get-grouped-users-list-basic-response";
import { Distinct, OnlyInLeft } from "../../../Utils/helpers";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import { PlatformMode } from "../../../Core/Api/Types/platform-mode";
import { useTranslation } from "react-i18next";

export type ModalSetFollowersElementProps = {
  objectIds: number[];
  currentFollowersIds: number[];
  objectType: "asset" | "workflow" | "order" | "orderItem";
  onConfirm?: () => void;
};

export type ModalSetFollowersProps = CommonModalProps<ModalSetFollowersElementProps>;

const ModalSetFollowers = forwardRef<any, ModalSetFollowersProps>((props, ref) => {
  const { t } = useTranslation();
  const { objectIds, currentFollowersIds, objectType, closeModal, onConfirm, ...modalProps } = props;

  // #region states
  const [followersGroupedDtos, setFollowersGroupedDtos] = useState<FollowersGroupedDto[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<FollowersGroupedDto[]>([]);
  const [followersUsersOptions, setFollowersUsersOptions] = useState<FollowerDto[]>([]);
  const [selectedFollowers, setSelectedFollowers] = useState<FollowerDto[]>([]);
  // #endregion

  const enqueueCustomSnackbar = useCustomSnackbar();
  const platformMode = cacheService.GetPlatformMode();

  // #region functions
  // compare
  const groupsOnlyInLeft = (left: FollowersGroupedDto[], right: FollowersGroupedDto[]) =>
    OnlyInLeft(left, right, (leftValue, rightValue) => leftValue.groupName === rightValue.groupName);
  // TODO : try to extract the logic and put it in a custom hook (useMultipleGroupedSelection ?) => same goes for handleSelectUsersChanged, we could add the logic of adding element not in groups in the custom hook
  const handleSelectGroupsChanged = (value: FollowersGroupedDto[]) => {
    // get previous state
    let newSelectedGroups = [...selectedGroups];
    let newSelectedFollowers = [...selectedFollowers];

    // get removed groups
    const removedGroups = groupsOnlyInLeft(selectedGroups, value);
    if (removedGroups.length > 0) {
      // remove group
      const removedGroupsNames = removedGroups.flatMap((groups) => groups.groupName);
      newSelectedGroups = newSelectedGroups.filter((group) => !removedGroupsNames.includes(group.groupName));
      // remove followers
      const removedFollowersIds = removedGroups.flatMap((group) => group.followers.flatMap((follower) => follower.id));
      newSelectedFollowers = newSelectedFollowers.filter((follower) => !removedFollowersIds.includes(follower.id));
    }

    // get added groups
    const addedGroups = groupsOnlyInLeft(value, selectedGroups);
    if (addedGroups.length > 0) {
      // add group
      newSelectedGroups = newSelectedGroups.concat(addedGroups);
      // add followers
      const addedFollowers = addedGroups.flatMap((groups) => groups.followers);
      newSelectedFollowers = newSelectedFollowers.concat(addedFollowers);
    }

    setSelectedGroups(newSelectedGroups.filter(Distinct));
    setSelectedFollowers(newSelectedFollowers.filter(Distinct));
  };

  const followersOnlyInLeft = (left: FollowerDto[], right: FollowerDto[]) =>
    OnlyInLeft(left, right, (leftValue, rightValue) => leftValue.id === rightValue.id);
  const handleSelectUsersChanged = (value: FollowerDto[]) => {
    // if clear
    if (value.length === 0) {
      setSelectedGroups([]);
      setSelectedFollowers([]);
      return;
    }
    // get previous state
    let newSelectedFollowers = [...selectedFollowers];

    // get removed followers
    const removedFollowers = followersOnlyInLeft(selectedFollowers, value);
    if (removedFollowers.length > 0) {
      // remove followers
      const removedFollowersIds = removedFollowers.flatMap((follower) => follower.id);
      newSelectedFollowers = newSelectedFollowers.filter((follower) => !removedFollowersIds.includes(follower.id));
    }

    // get added followers
    const addedFollowers = followersOnlyInLeft(value, selectedFollowers);
    if (addedFollowers.length > 0) {
      // add followers
      newSelectedFollowers = newSelectedFollowers.concat(addedFollowers);
    }

    setSelectedFollowers(newSelectedFollowers.filter(Distinct));
  };

  const handleSetFollowers = () => {
    const followersIds = selectedFollowers.flatMap((follower) => follower.id);

    for (let i = 0; i < objectIds.length; ++i) {
      switch (objectType) {
        case "asset":
          assetService.SetFollowers({ id: objectIds[i], followerUserIds: followersIds, message: "" }).then(() => {
            onConfirm && onConfirm();
            handleCloseAfterCall(objectIds[i]);
          });
          break;
        case "order":
          orderService
            .SetOrderFollowers({ id: objectIds[i], followerUserIds: followersIds })
            .then(() => handleCloseAfterCall(objectIds[i]));
          break;
        case "orderItem":
          orderService
            .SetOrderItemFollowers({ id: objectIds[i], followerUserIds: followersIds })
            .then(() => handleCloseAfterCall(objectIds[i]));
          break;
        case "workflow":
          workflowService
            .SetWorkflowInstanceFollowers({ id: objectIds[i], followerUserIds: followersIds })
            .then(() => handleCloseAfterCall(objectIds[i]));
          break;
      }
    }
  };

  const handleCloseAfterCall = (id: number) => {
    enqueueCustomSnackbar(t("alert:success.followersSetForObject", { objectType, id }), "success");
    closeModal();
  };
  // #endregion

  useEffect(() => {
    userService.GetModalSetFollowersData().then((data) => {
      setFollowersGroupedDtos(data);
      const _followersUsersOptions = data
        .flatMap((x) => x.followers)
        .sort((a, b) => {
          const nameA = a.name.toLowerCase();
          const nameB = b.name.toLowerCase();
          return nameA < nameB ? -1 : nameA > nameB ? 1 : a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
        });

      setFollowersUsersOptions(_followersUsersOptions);

      if (currentFollowersIds && currentFollowersIds.length > 0) {
        const curFollowers = _followersUsersOptions.filter((x) => currentFollowersIds.includes(x.id));
        setSelectedFollowers(curFollowers);
      }
    });
  }, [objectIds, currentFollowersIds]);

  return (
    <Modal
      title={t("modal:title.assignFollowers")}
      reference={ref}
      closeModal={closeModal}
      width={600}
      footer={
        <Stack direction="row" justifyContent="space-between">
          <Button variant="outlined" onClick={closeModal}>
            {t("common:cancel")}
          </Button>
          <Button variant="outlined" color="success" onClick={handleSetFollowers}>
            {t("common:setFollowers")}
          </Button>
        </Stack>
      }
    >
      <Stack spacing={2}>
        {!(objectType === "asset" && platformMode === PlatformMode.Adam) && (
          <Autocomplete
            multiple
            disableCloseOnSelect
            popupIcon={<ExpandMoreRoundedIcon />}
            options={followersGroupedDtos}
            getOptionLabel={(option) => option.groupName}
            value={selectedGroups}
            onChange={(event, value) => handleSelectGroupsChanged(value)}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("field:label.fromGroups")}
                placeholder={t("field:placeholder.selectGroupsOfFollowers")}
              />
            )}
          />
        )}
        <Autocomplete
          multiple
          popupIcon={<ExpandMoreRoundedIcon />}
          options={followersUsersOptions}
          getOptionLabel={(option) => `${option.name} - ${option.login} (${option.id})`}
          isOptionEqualToValue={(option, value) =>
            option.name.includesAllWords(value.name) || option.login.includesAllWords(value.login)
          }
          value={selectedFollowers}
          onChange={(event, value) => handleSelectUsersChanged(value)}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t("field:label.fromUsers")}
              placeholder={t("field:placeholder.selectFollowers")}
            />
          )}
        />
      </Stack>
    </Modal>
  );
});

export default ModalSetFollowers;
